import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { format, startOfWeek, addWeeks } from "date-fns";
import { ShoppingListApi } from "../api/ShoppingListApi";
import { useUserSettings } from "./UserSettingsContext";
import { DAY_TO_DATE_FNS_INDEX } from "../constants/MealPlanPage";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const ShoppingListContext = createContext();

export const useShoppingList = () => {
  return useContext(ShoppingListContext);
};

export const ShoppingListProvider = ({ children }) => {
  const { weekStartDay } = useUserSettings();
  const [shoppingListObject, setShoppingListObject] = useState([]);
  const [shoppingWindow, setShoppingWindow] = useState("THIS_WEEK");
  const [customWeeks, setCustomWeeks] = useState(3);

  const getWeekStartDates = useCallback(() => {
    const weekStartsOn = DAY_TO_DATE_FNS_INDEX[weekStartDay] ?? 1;
    const thisWeekStart = startOfWeek(new Date(), { weekStartsOn });

    if (shoppingWindow === "THIS_WEEK") {
      return [format(thisWeekStart, "yyyy-MM-dd")];
    }
    if (shoppingWindow === "THIS_AND_NEXT") {
      return [
        format(thisWeekStart, "yyyy-MM-dd"),
        format(addWeeks(thisWeekStart, 1), "yyyy-MM-dd"),
      ];
    }
    // CUSTOM: customWeeks weeks starting from this week
    return Array.from({ length: customWeeks }, (_, i) =>
      format(addWeeks(thisWeekStart, i), "yyyy-MM-dd")
    );
  }, [shoppingWindow, customWeeks, weekStartDay]);

  // Fetch
  const fetchShoppingList = useCallback(async () => {
    try {
      const weekStartDates = getWeekStartDates();
      const response = await ShoppingListApi.getShoppingList(weekStartDates);
      setShoppingListObject(response.shoppingListEntries);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to fetch shopping list", error, { correlationId });

      toast.error(errorMessage);
    }
  }, [getWeekStartDates]);

  // add-items
  const addItemsToShoppingList = async (itemsToAdd) => {
    try {
      const dates = getWeekStartDates();
      const weekStartDate = dates[dates.length - 1];
      await ShoppingListApi.addItemsToShoppingList(itemsToAdd, weekStartDate);
      await fetchShoppingList();
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to add items to shopping list", error, {
        correlationId,
      });

      toast.error(errorMessage);
    }
  };

  // clear shopping list
  const clearShoppingList = async () => {
    const previousList = shoppingListObject;

    setShoppingListObject([]);

    try {
      await ShoppingListApi.clearShoppingList();
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to clear shopping list", error, { correlationId });

      toast.error(errorMessage);

      // Rollback to previous state
      setShoppingListObject(previousList);
    }
  };

  // remove-items (by id)
  const removeItemsFromShoppingListById = async (id) => {
    const previousList = shoppingListObject;

    setShoppingListObject((prevList) =>
      prevList.filter((item) => item.itemId !== id),
    );

    try {
      await ShoppingListApi.removeItemsFromShoppingListByIds([id]);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to remove item from shopping list", error, {
        correlationId,
        itemId: id,
      });

      toast.error(errorMessage);

      // Rollback to previous state
      setShoppingListObject(previousList);
    }
  };

  // toggle item check
  const toggleItemCheck = async (id) => {
    setShoppingListObject((prevList) =>
      prevList.map((item) =>
        item.itemId === id ? { ...item, checked: !item.checked } : item,
      ),
    );

    try {
      await ShoppingListApi.toggleItemCheck(id);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to toggle item check", error, {
        correlationId,
        itemId: id,
      });

      toast.error(errorMessage);

      // Rollback - toggle back
      setShoppingListObject((prevList) =>
        prevList.map((item) =>
          item.itemId === id ? { ...item, checked: !item.checked } : item,
        ),
      );
    }
  };

  const value = {
    shoppingListObject,
    addItemsToShoppingList,
    clearShoppingList,
    removeItemsFromShoppingListById,
    toggleItemCheck,
    fetchShoppingList,
    shoppingWindow,
    setShoppingWindow,
    customWeeks,
    setCustomWeeks,
    weekStartDates: getWeekStartDates(),
  };

  useEffect(() => {
    fetchShoppingList();
  }, [fetchShoppingList]);

  return (
    <ShoppingListContext.Provider value={value}>
      {children}
    </ShoppingListContext.Provider>
  );
};
